import type { Category } from "../api.js";
import type { AiEstimate, MaterialGrade, PriceSnapshot } from "../estimate.js";
import { getFormula } from "../materials/registry.js";
import type { MaterialInput } from "../materials/types.js";

/**
 * คำนวณราคาประเมิน (pure, ไม่ใช้ Date/random) ใช้ร่วม client และ server
 *
 * การตัดสินใจสำคัญ
 * - ข้อมูลอ้างอิงมีแค่ราคาวัสดุรวมต่อหน่วยงาน ไม่มีราคารายรายการ จึงไม่แต่งราคารายรายการ
 * - โหมด reference: วัสดุ = materialRate x workQty ; บรรทัดวัสดุแสดงเฉพาะปริมาณ (unitPrice/total = null)
 * - โหมด itemized: เข้าเมื่อมี overrides.unitPrice ของบรรทัดที่ยังอยู่อย่างน้อยหนึ่งบรรทัด ; รวมเฉพาะบรรทัดที่มีราคา
 * - removed: ลบบรรทัดออกจากรายการเสมอ ในโหมด reference ไม่กระทบราคา (ราคาเป็นอัตรารวม) และใส่ warning removed_in_reference
 * - เงินทุกช่องปัดสตางค์ด้วย roundMoney แล้วรวมต่อจากค่าที่ปัดแล้ว grand จึงเท่ากับผลรวมที่แสดงเสมอ
 */

export type MaterialMode = "reference" | "itemized";
export type Source = "ai" | "ref" | "user";
export type MarketPosition = "low" | "mid" | "high" | "out";

export const VAT_RATE = 0.07;
export const DEFAULT_WASTE_PCT = 0.05;
export const DEFAULT_WALL_HEIGHT_M = 2.7;

export type PricedCategory = PriceSnapshot & { material_components?: Category["material_components"] };

export interface EstimateDims {
  width_m?: number | null;
  length_m?: number | null;
  height_m?: number | null;
  count?: number | null;
  area_m2?: number | null;
  doorDeduct_m2?: number;
}

export interface CustomLineInput {
  key: string;
  name_th: string;
  qty: number;
  unit: string;
  unitPrice: number;
}

export interface EstimateGroupInput {
  /** ตัวระบุกลุ่มจากฝั่งเรียก (ส่งผ่านไปยังผลลัพธ์) */
  id?: string;
  label_th?: string;
  category: PricedCategory;
  plane: "ceiling" | "floor" | "wall";
  grade: MaterialGrade;
  dims: EstimateDims;
  /** แหล่งของ dims ค่าเริ่มต้น "ai" */
  dimsSource?: "ai" | "user";
  wastePct?: number;
  tileSizeM?: number;
  overrides?: {
    laborRate?: number;
    materialRate?: number;
    qty?: Record<string, number>;
    unitPrice?: Record<string, number>;
    removed?: string[];
  };
  custom?: CustomLineInput[];
}

export interface EstimateInput {
  groups: EstimateGroupInput[];
  discount?: { type: "amount" | "percent"; value: number };
  /** เปอร์เซ็นต์ เช่น 10 = 10% */
  overheadPct?: number;
  vat?: boolean;
}

export type WarningCode =
  | "missing_dims"
  | "zero_qty"
  | "formula_unavailable"
  | "unpriced_lines"
  | "removed_in_reference"
  | "invalid_custom";

export interface EstimateWarning {
  code: WarningCode;
  /** จำนวนรายการที่เกี่ยวข้อง (ถ้ามี) */
  count?: number;
}

export interface MaterialLineResult {
  key: string;
  name_th: string;
  qty: number;
  unit: string;
  qty_basis?: string;
  /** ที่มาของปริมาณ: ai = สูตร, user = แก้เอง/เพิ่มเอง */
  source: "ai" | "user";
  unitPrice: number | null;
  total: number | null;
  /** ที่มาของราคาต่อหน่วย (null = ไม่มีราคา) */
  priceSource: "user" | null;
  custom: boolean;
}

export interface MarketRange {
  min: number;
  max: number;
  position: MarketPosition;
}

export interface GroupResult {
  id?: string;
  label_th?: string;
  category_id: string;
  plane: EstimateGroupInput["plane"];
  unit: Category["unit"];
  grade: MaterialGrade;
  workQty: number;
  workQtySource: "ai" | "user";
  labor: { rate: number; rateSource: "ref" | "user"; total: number; source: "ref" | "user" };
  materials: {
    mode: MaterialMode;
    lines: MaterialLineResult[];
    /** อัตราวัสดุต่อหน่วยงาน (เฉพาะ reference) */
    rate: number | null;
    rateSource: "ref" | "user" | null;
    /** ส่วนที่มาจากอัตรา (reference) หรือรายการที่ใส่ราคา (itemized) ไม่รวม custom */
    baseTotal: number;
    customTotal: number;
    total: number;
    source: "ref" | "user";
  };
  subtotal: number;
  perUnit: number | null;
  market: MarketRange;
  warnings: EstimateWarning[];
}

export interface EstimateTotals {
  materials: number;
  labor: number;
  subtotal: number;
  overhead: number;
  discount: number;
  beforeVat: number;
  vat: number;
  grand: number;
  /** ผลรวม workQty และหน่วย เมื่อทุกกลุ่มหน่วยเดียวกันเท่านั้น */
  workQty: number | null;
  unit: Category["unit"] | null;
  perUnit: number | null;
  /** ช่วงราคาตลาดเทียบกับ subtotal (ก่อนค่าดำเนินการ ส่วนลด VAT) */
  market: MarketRange;
}

export interface EstimateResult {
  groups: GroupResult[];
  totals: EstimateTotals;
}

/* ---------- helpers ---------- */

/** ปัดสตางค์ 2 ตำแหน่งแบบกัน floating point */
export function roundMoney(x: number): number {
  return Math.round((x + Number.EPSILON) * 100) / 100;
}

/** VAT 7% ที่บวกเพิ่มจากราคาก่อน VAT (ใบเสนอราคา) ปิดแล้ว vat = 0 */
export function addVat(beforeVat: number, enabled: boolean): { vat: number; grand: number } {
  const vat = enabled ? roundMoney(beforeVat * VAT_RATE) : 0;
  return { vat, grand: roundMoney(beforeVat + vat) };
}

/** แยก VAT 7% ที่รวมอยู่ในราคาแล้ว (ราคาแพ็กเกจ ใบเสร็จรับเงิน) beforeVat + vat = total เสมอ */
export function splitVatInclusive(total: number): { beforeVat: number; vat: number; total: number } {
  const vat = roundMoney((total * VAT_RATE) / (1 + VAT_RATE));
  return { beforeVat: roundMoney(total - vat), vat, total: roundMoney(total) };
}

/** ปัดปริมาณงานเป็น 4 ตำแหน่ง ตัด noise ของ floating point */
function roundQty(x: number): number {
  return Math.round((x + Number.EPSILON) * 10000) / 10000;
}

/** economy = min, standard = ค่ากลาง, premium = max */
export function priceByGrade(min: number, max: number, grade: MaterialGrade): number {
  if (grade === "economy") return min;
  if (grade === "premium") return max;
  return (min + max) / 2;
}

function num(v: unknown): number | undefined {
  return typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : undefined;
}

function positionOf(value: number, min: number, max: number): MarketPosition {
  if (value < min || value > max) return "out";
  const span = max - min;
  if (span <= 0) return "mid";
  const t = (value - min) / span;
  if (t < 1 / 3) return "low";
  if (t > 2 / 3) return "high";
  return "mid";
}

function marketRange(value: number, min: number, max: number): MarketRange {
  const lo = roundMoney(min);
  const hi = roundMoney(max);
  return { min: lo, max: hi, position: positionOf(value, lo, hi) };
}

type Missing = { qty: number; missing: boolean };

function workQtyOf(g: EstimateGroupInput): Missing {
  const d = g.dims;
  const unit = g.category.unit;
  if (unit === "sqm") {
    const area = num(d.area_m2);
    if (g.plane === "wall") {
      if (area !== undefined) return { qty: area, missing: false };
      const l = num(d.length_m);
      const h = num(d.height_m);
      if (l === undefined || h === undefined) return { qty: 0, missing: true };
      return { qty: Math.max(0, l * h - (num(d.doorDeduct_m2) ?? 0)), missing: false };
    }
    if (area !== undefined) return { qty: area, missing: false };
    const w = num(d.width_m);
    const l = num(d.length_m);
    if (w === undefined || l === undefined) return { qty: 0, missing: true };
    return { qty: w * l, missing: false };
  }
  const c = num(d.count);
  return c === undefined ? { qty: 0, missing: true } : { qty: c, missing: false };
}

function formulaInput(g: EstimateGroupInput, workQty: number): MaterialInput {
  const input: MaterialInput = { area: workQty };
  const w = num(g.dims.width_m);
  const l = num(g.dims.length_m);
  // ผนังใช้พื้นที่อย่างเดียว ไม่ส่ง width/length เพราะ length คือความยาวผนัง
  if (g.plane !== "wall" && w !== undefined && l !== undefined) {
    input.width = w;
    input.length = l;
  }
  const waste = num(g.wastePct);
  input.wastePct = waste ?? DEFAULT_WASTE_PCT;
  const tile = g.tileSizeM;
  if (typeof tile === "number" && Number.isFinite(tile) && tile > 0) input.tileSizeM = tile;
  return input;
}

function computeGroup(g: EstimateGroupInput): GroupResult {
  const cat = g.category;
  const warnings: EstimateWarning[] = [];
  const { qty, missing } = workQtyOf(g);
  const workQty = roundQty(qty);
  if (missing) warnings.push({ code: "missing_dims" });
  else if (workQty <= 0) warnings.push({ code: "zero_qty" });

  const ov = g.overrides ?? {};
  const laborOverride = num(ov.laborRate);
  const laborRate = roundMoney(laborOverride ?? priceByGrade(cat.labor_min_thb, cat.labor_max_thb, g.grade));
  const laborSource = laborOverride !== undefined ? "user" : "ref";
  const laborTotal = roundMoney(laborRate * workQty);

  // บรรทัดวัสดุจากสูตร (เฉพาะงานหน่วย sqm ที่มีปริมาณ)
  const removed = new Set(ov.removed ?? []);
  let base: MaterialLineResult[] = [];
  let removedCount = 0;
  if (cat.unit === "sqm" && workQty > 0) {
    try {
      const raw = getFormula(cat.category_id)(formulaInput(g, workQty));
      for (const ln of raw) {
        if (removed.has(ln.key)) {
          removedCount++;
          continue;
        }
        const qOv = num(ov.qty?.[ln.key]);
        base.push({
          key: ln.key,
          name_th: ln.name_th,
          qty: qOv ?? ln.qty,
          unit: ln.unit,
          ...(ln.qty_basis !== undefined ? { qty_basis: ln.qty_basis } : {}),
          source: qOv !== undefined ? "user" : "ai",
          unitPrice: null,
          total: null,
          priceSource: null,
          custom: false,
        });
      }
    } catch {
      base = [];
      warnings.push({ code: "formula_unavailable" });
    }
  }

  const priceOv = ov.unitPrice ?? {};
  const itemized = base.some((l) => num(priceOv[l.key]) !== undefined);
  const mode: MaterialMode = itemized ? "itemized" : "reference";

  let baseTotal = 0;
  let rate: number | null = null;
  let rateSource: "ref" | "user" | null = null;
  if (itemized) {
    let unpriced = 0;
    base = base.map((l) => {
      const p = num(priceOv[l.key]);
      if (p === undefined) {
        unpriced++;
        return l;
      }
      return { ...l, unitPrice: p, total: roundMoney(l.qty * p), priceSource: "user" as const };
    });
    baseTotal = roundMoney(base.reduce((s, l) => s + (l.total ?? 0), 0));
    if (unpriced > 0) warnings.push({ code: "unpriced_lines", count: unpriced });
  } else {
    const matOverride = num(ov.materialRate);
    rate = roundMoney(matOverride ?? priceByGrade(cat.material_min_thb, cat.material_max_thb, g.grade));
    rateSource = matOverride !== undefined ? "user" : "ref";
    baseTotal = roundMoney(rate * workQty);
    if (removedCount > 0) warnings.push({ code: "removed_in_reference", count: removedCount });
  }

  const customLines: MaterialLineResult[] = [];
  let invalidCustom = 0;
  for (const c of g.custom ?? []) {
    const q = num(c.qty);
    const p = num(c.unitPrice);
    if (q === undefined || p === undefined) {
      invalidCustom++;
      continue;
    }
    customLines.push({
      key: c.key,
      name_th: c.name_th,
      qty: q,
      unit: c.unit,
      source: "user",
      unitPrice: p,
      total: roundMoney(q * p),
      priceSource: "user",
      custom: true,
    });
  }
  if (invalidCustom > 0) warnings.push({ code: "invalid_custom", count: invalidCustom });
  const customTotal = roundMoney(customLines.reduce((s, l) => s + (l.total ?? 0), 0));
  const materialsTotal = roundMoney(baseTotal + customTotal);
  const subtotal = roundMoney(laborTotal + materialsTotal);

  const matUser = itemized || rateSource === "user" || customLines.length > 0;
  return {
    ...(g.id !== undefined ? { id: g.id } : {}),
    ...(g.label_th !== undefined ? { label_th: g.label_th } : {}),
    category_id: cat.category_id,
    plane: g.plane,
    unit: cat.unit,
    grade: g.grade,
    workQty,
    workQtySource: g.dimsSource ?? "ai",
    labor: { rate: laborRate, rateSource: laborSource, total: laborTotal, source: laborSource },
    materials: {
      mode,
      lines: [...base, ...customLines],
      rate,
      rateSource,
      baseTotal,
      customTotal,
      total: materialsTotal,
      source: matUser ? "user" : "ref",
    },
    subtotal,
    perUnit: workQty > 0 ? roundMoney(subtotal / workQty) : null,
    market: marketRange(
      subtotal,
      (cat.labor_min_thb + cat.material_min_thb) * workQty,
      (cat.labor_max_thb + cat.material_max_thb) * workQty,
    ),
    warnings,
  };
}

export function computeEstimate(input: EstimateInput): EstimateResult {
  const groups = input.groups.map(computeGroup);

  const materials = roundMoney(groups.reduce((s, g) => s + g.materials.total, 0));
  const labor = roundMoney(groups.reduce((s, g) => s + g.labor.total, 0));
  const subtotal = roundMoney(materials + labor);

  const ovh = num(input.overheadPct) ?? 0;
  const overhead = roundMoney((subtotal * ovh) / 100);
  const gross = roundMoney(subtotal + overhead);

  let discount = 0;
  const d = input.discount;
  if (d && Number.isFinite(d.value) && d.value > 0) {
    discount = d.type === "percent" ? (gross * Math.min(d.value, 100)) / 100 : Math.min(d.value, gross);
  }
  discount = roundMoney(discount);

  const beforeVat = roundMoney(gross - discount);
  const vat = input.vat ? roundMoney(beforeVat * VAT_RATE) : 0;
  const grand = roundMoney(beforeVat + vat);

  const units = new Set(groups.map((g) => g.unit));
  const unit = units.size === 1 ? [...units][0]! : null;
  const sumQty = unit ? roundQty(groups.reduce((s, g) => s + g.workQty, 0)) : null;
  const mMin = groups.reduce((s, g) => s + g.market.min, 0);
  const mMax = groups.reduce((s, g) => s + g.market.max, 0);

  return {
    groups,
    totals: {
      materials,
      labor,
      subtotal,
      overhead,
      discount,
      beforeVat,
      vat,
      grand,
      workQty: sumQty,
      unit,
      perUnit: sumQty && sumQty > 0 ? roundMoney(grand / sumQty) : null,
      market: marketRange(subtotal, mMin, mMax),
    },
  };
}

/* ---------- AI -> group inputs ---------- */

export type AssumptionCode = "default_wall_height" | "wall_width_as_length" | "missing_snapshot";

export interface Assumption {
  code: AssumptionCode;
  /** ลำดับของ work_areas ใน AI */
  areaIndex: number;
  /** ค่าที่เติมให้ (ถ้ามี) */
  value?: number;
}

export interface AiGroupInputs {
  groups: EstimateGroupInput[];
  assumptions: Assumption[];
}

const nn = (v: number | null | undefined): number | undefined => (v === null || v === undefined ? undefined : v);

/**
 * แปลง work_areas ของ AI เป็น group input
 * - dimension ที่เป็น null เป็น undefined
 * - plane=wall และไม่มี height -> เติม 2.7 (assumption default_wall_height)
 * - plane=wall มี width แต่ไม่มี length -> ใช้ width เป็นความยาวผนัง (assumption wall_width_as_length)
 * - ไม่พบ snapshot ของหมวด -> ข้ามพื้นที่นั้น (assumption missing_snapshot)
 * groups[i].id = "area-<index>" ตรงกับ areaIndex
 */
export function aiToGroupInputs(ai: AiEstimate, snapshots: readonly PriceSnapshot[], grade: MaterialGrade): AiGroupInputs {
  const groups: EstimateGroupInput[] = [];
  const assumptions: Assumption[] = [];
  ai.work_areas.forEach((area, areaIndex) => {
    const snap = snapshots.find((s) => s.category_id === area.category_id);
    if (!snap) {
      assumptions.push({ code: "missing_snapshot", areaIndex });
      return;
    }
    const e = area.estimated_dimensions;
    const dims: EstimateDims = {
      width_m: nn(e.width_m),
      length_m: nn(e.length_m),
      height_m: nn(e.height_m),
      count: nn(e.count),
    };
    if (area.plane === "wall" && snap.unit === "sqm") {
      if (dims.length_m == null && dims.width_m != null) {
        dims.length_m = dims.width_m;
        assumptions.push({ code: "wall_width_as_length", areaIndex, value: dims.width_m });
      }
      if (dims.height_m == null) {
        dims.height_m = DEFAULT_WALL_HEIGHT_M;
        assumptions.push({ code: "default_wall_height", areaIndex, value: DEFAULT_WALL_HEIGHT_M });
      }
    }
    groups.push({ id: `area-${areaIndex}`, label_th: area.label_th, category: snap, plane: area.plane, grade, dims, dimsSource: "ai" });
  });
  return { groups, assumptions };
}
